import React from "react";
import { Text } from "@showitinc/showit-wc";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useAccountState, isContributing as getIsContributing } from "../hooks/query/useAccount";
import UserActions from "../actions/UserActions";
import AppConstants from "../constants/AppConstants";
import Button from "./base/Button";
import SubscribeButton from "./SubscribeButton";
import DesignViewFooter from "./DesignViewFooter";
import InfoSection from "./InfoSection";
import ShowitNameLogo from "./ShowitNameLogo";
import useOpenAccount, { PATH_SUBSCRIPTION } from "../hooks/useOpenAccount";
import When from "./utils/When";
import uiPrefix from "../utils/UiPrefixUtils";
import styles from "./SubscribeView.module.css";

const logout = () => {
  UserActions.logout();
};

function TrialEndedMessage({ user }) {
  const isExtendTrialButtonEnabled = useFeatureFlagEnabled("extend-trial-button-design-app");

  return (
    <>
      <Text variant="heading-1" variant-style="strong">
        Your Trial Has Ended
      </Text>
      <Text>
        {user.get("first_name")}, your access to Showit has been disabled.
        <br />
        Please subscribe to continue using Showit.
      </Text>
      <div className={styles.trialEndedMessage}>
        <SubscribeButton>Subscribe To Showit</SubscribeButton>
        {isExtendTrialButtonEnabled && (
          <Button
            look="primary"
            data-e2e={uiPrefix("extend-trial-btn")}
            onClick={() => {
              UserActions.trackEvent({
                event: "extend-trial",
                meta: {
                  source: "design-app",
                },
              });
            }}
          >
            Extend Trial
          </Button>
        )}
      </div>
    </>
  );
}

function InactiveMessage({ user }) {
  const [openSubscription] = useOpenAccount({ path: PATH_SUBSCRIPTION });
  const isContributing = getIsContributing();
  return (
    <>
      <Text variant="heading-1" variant-style="strong">
        Your Trial Has Ended
      </Text>
      <Text>
        {user.get("first_name")}, your access to Showit has been disabled.
        <br />
        Please resubscribe to continue using Showit.
      </Text>
      <When it={!isContributing}>
        <Button
          look="primary"
          data-e2e={uiPrefix("re-subscribe-btn")}
          className={uiPrefix("re-subscribe-btn")}
          onClick={openSubscription}
        >
          Resubscribe
        </Button>
      </When>
    </>
  );
}

function MessageContainer({ children }) {
  return (
    <div className={styles.viewCentered}>
      <ShowitNameLogo />
      {children}
    </div>
  );
}

function SubscribeView() {
  const user = useAccountState();
  const status = user.get("status");

  const message =
    status === AppConstants.USER_STATUS_INACTIVE ? (
      <InactiveMessage user={user} />
    ) : (
      <TrialEndedMessage user={user} />
    );

  return (
    <div>
      <MessageContainer>{message}</MessageContainer>
      <DesignViewFooter>
        <div className={styles.footer}>
          <Button look="link" onClick={logout}>
            Sign Out {user.get("first_name")} {user.get("last_name")}
          </Button>
        </div>
        <InfoSection width={AppConstants.PANEL_WIDTH_OPEN} />
      </DesignViewFooter>
    </div>
  );
}

export default SubscribeView;
